import { useRouterState } from '@tanstack/react-router';
import * as React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useOneTimeEvent } from '@/hooks/useOneTimeEvent';
import { useUser } from '@/hooks/useUser';
import { getLocalStorageItem } from '@/utils/localStorage';

interface RouteStaticData {
  hideLeftSidebar?: boolean;
  hideLeftMainHeader?: boolean;
}

interface LayoutContextType {
  hideLeftSidebar: boolean;
  hideLeftMainHeader: boolean;
  isLargeScreen: boolean;
  isSmallScreen: boolean;
  isSidebarOpen: boolean;
  setIsSidebarOpen: () => void;
  isOnboardingSidebarOpen: boolean;
  setIsOnboardingSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showOnboardingSidebar: boolean;
  dismissOnboardingChecklist: () => void;
}

const LayoutContext = createContext<LayoutContextType | null>(null);

export const LayoutProvider = ({ children }: { children: React.ReactNode }) => {
  const isLargeScreen = useMediaQuery('(min-width: 1024px)');
  const { isSuperAdmin, routeOrganizationName } = useUser();

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    return getLocalStorageItem('sidebarOpen', true);
  });

  const [isOnboardingSidebarOpen, setIsOnboardingSidebarOpen] = useState(() => {
    return getLocalStorageItem('onboardingSidebarOpen', true);
  });

  // Dismissed means completed and closed. Keyed by organization because one
  // browser signs into several. Uses the URL slug because this provider mounts
  // above Auth0Provider, where the token-derived `organizationId` is always null.
  const { markDismissed: dismissOnboardingChecklist, state: onboardingChecklistEvent } = useOneTimeEvent(
    routeOrganizationName ? `onboarding-checklist:${routeOrganizationName}` : 'onboarding-checklist:__pending__'
  );
  const isOnboardingDismissed = onboardingChecklistEvent.status === 'dismissed';

  // The browser-wide flag that preceded the per-organization key is retired.
  useEffect(() => {
    localStorage.removeItem('onboarding-checklist-dismissed');
  }, []);
  const showOnboardingSidebar = isSuperAdmin && !isOnboardingDismissed;

  // Check if any route in the current matches has layout-related staticData set to true
  const hideLeftSidebar = useRouterState({
    select: (s) => s.matches.some((match) => (match.staticData as RouteStaticData)?.hideLeftSidebar === true),
  });
  const hideLeftMainHeader = useRouterState({
    select: (s) => s.matches.some((match) => (match.staticData as RouteStaticData)?.hideLeftMainHeader === true),
  });

  useEffect(() => {
    localStorage.setItem('sidebarOpen', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  useEffect(() => {
    localStorage.setItem('onboardingSidebarOpen', JSON.stringify(isOnboardingSidebarOpen));
  }, [isOnboardingSidebarOpen]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev: boolean) => !prev);
  }, []);

  const value = {
    dismissOnboardingChecklist,
    hideLeftMainHeader,
    hideLeftSidebar,
    isLargeScreen,
    isOnboardingSidebarOpen: isOnboardingSidebarOpen && isSuperAdmin,
    isSidebarOpen,
    isSmallScreen: !isLargeScreen,
    setIsOnboardingSidebarOpen,
    setIsSidebarOpen: toggleSidebar,
    showOnboardingSidebar,
  };

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};
